import { useState, type ReactElement, type ReactNode } from 'react';
import type { AccountPlanDto } from '@gr/core/accounts/plans/dto';
import type { PlanLimit } from '@gr/core/accounts/plans/limits';
import { useT } from '../../../../app/di';
import { formatDate, formatFileSize, formatNumber } from '../../../../shared/format';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { Badge } from '../../../../ui-kit/Badge/Badge';
import { Button } from '../../../../ui-kit/Button/Button';
import { ProgressBar } from '../../../../ui-kit/ProgressBar/ProgressBar';
import { amountBytes, usageRows } from '../model/plan-usage';
import styles from './PlanUsage.module.scss';

export interface PlanUsageProps {
  account: AccountPlanDto;
  /** Подсказка, кто меняет тариф, — в профиле пользователя. */
  showChangeHint?: boolean;
  /** PLAN-UC-5: сводка по форматам блога — раскрывается по кнопке (в профиле). */
  blogFormats?(blogId: string): ReactNode;
}

/** PLAN-UC-4, PLAN-UC-5: план и сколько израсходовано по каждому лимиту. */
export function PlanUsage({ account, showChangeHint = false, blogFormats }: PlanUsageProps): ReactElement {
  const { t, locale } = useT();
  const [formatsOf, setFormatsOf] = useState<string | null>(null);
  const amount = (limit: PlanLimit, value: number): string => {
    const bytes = amountBytes(limit, value);
    return bytes === null ? formatNumber(value, locale) : formatFileSize(bytes, locale);
  };

  return (
    <div className="u-stack">
      <div className={styles.head}>
        <strong className={styles.name}>{t('plans.usage.plan', { name: account.plan.name })}</strong>
        {account.plan.description ? <p className="u-muted u-small">{account.plan.description}</p> : null}
        <p className="u-muted u-small">{account.assignedAt ? t('plans.usage.assignedAt', { at: formatDate(account.assignedAt, locale) }) : t('plans.usage.defaultPlan')}</p>
      </div>
      {account.enforced ? null : <Alert tone="info">{t('plans.usage.notEnforced')}</Alert>}
      <ul className={styles.rows}>
        {usageRows(account).map((row) => {
          const label = t(`plans.usageLabels.${row.limit}`);
          return (
            <li key={row.limit} className={styles.row} data-limit={row.limit}>
              <div className={styles.rowHead}>
                <span className={styles.label}>{label}</span>
                <span className={styles.value}>
                  {row.max === null ? `${amount(row.limit, row.used)} · ${t('plans.usage.unlimited')}` : t('plans.usage.ofMax', { used: amount(row.limit, row.used), max: amount(row.limit, row.max) })}
                  {row.exhausted ? (
                    <>
                      {' '}
                      <Badge tone="danger">{t('plans.usage.exhausted')}</Badge>
                    </>
                  ) : null}
                </span>
              </div>
              {row.ratio !== null ? <ProgressBar value={row.ratio} label={label} /> : null}
            </li>
          );
        })}
      </ul>
      <div className="u-stack u-gap-1">
        <p className={styles.subtitle}>{t('plans.usage.storageTitle')}</p>
        {account.storage.length === 0 ? (
          <p className="u-muted u-small">{t('plans.usage.noBlogs')}</p>
        ) : (
          <ul className={styles.traffic} data-testid="storage-by-blog">
            {account.storage.map((blog) => (
              <li key={blog.blogId} className="u-stack u-gap-1">
                <div className="u-row">
                  <span>{t('plans.usage.storageRow', { name: blog.name, used: formatFileSize(blog.usedMb * 1024 ** 2, locale) })}</span>
                  {blogFormats ? (
                    <Button size="sm" variant="ghost" aria-expanded={formatsOf === blog.blogId} onClick={() => setFormatsOf(formatsOf === blog.blogId ? null : blog.blogId)}>
                      {formatsOf === blog.blogId ? t('plans.usage.hideFormats') : t('plans.usage.showFormats')}
                    </Button>
                  ) : null}
                </div>
                {blogFormats && formatsOf === blog.blogId ? blogFormats(blog.blogId) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="u-stack u-gap-1">
        <p className={styles.subtitle}>{t('plans.usage.trafficTitle')}</p>
        {account.traffic.length === 0 ? (
          <p className="u-muted u-small">{t('plans.usage.noBlogs')}</p>
        ) : (
          <ul className={styles.traffic}>
            {account.traffic.map((blog) => (
              <li key={blog.blogId}>{t('plans.usage.trafficRow', { name: blog.name, used: formatFileSize(blog.usedGb * 1024 ** 3, locale) })}</li>
            ))}
          </ul>
        )}
      </div>
      {showChangeHint ? <p className="u-muted u-small">{t('plans.usage.changeHint')}</p> : null}
    </div>
  );
}
